import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTutorial, TUTORIAL_STEPS } from './TutorialContext';
import useTranslations from '../../Hooks/useTranslations';
import translations from './translations.json';
import { FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import './css/tutorialOverlay.css';

function TutorialDot({ x, y, isActive }) {
  return (
    <div
      className="tutorial-component-dot-wrapper"
      style={{ left: x, top: y }}
    >
      <span className={`tutorial-component-dot-pulse-ring ${isActive ? 'active' : ''}`} />
      <span className={`tutorial-component-dot-inner ${isActive ? 'active' : ''}`} />
    </div>
  );
}

function TutorialTooltip({
  stepIndex,
  totalSteps,
  title,
  description,
  position,
  targetRect,
  onNext,
  onPrev,
  onFinish,
  t,
}) {
  const tooltipRef = useRef(null);
  const [style, setStyle] = useState({ position: 'fixed', zIndex: 2010, opacity: 0 });
  const isLast = stepIndex === totalSteps - 1;
  const isFirst = stepIndex === 0;

  useEffect(() => {
    if (!targetRect) return;

    requestAnimationFrame(() => {
      const OFFSET = 16;
      const TOOLTIP_W = 288;
      const tooltipH = tooltipRef.current?.offsetHeight || 200;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const MARGIN = 12;

      let left = 0;
      let top = 0;

      switch (position) {
        case 'right':
          left = targetRect.right + OFFSET;
          top = targetRect.top + targetRect.height / 2 - tooltipH / 2;
          break;
        case 'left':
          left = targetRect.left - OFFSET - TOOLTIP_W;
          top = targetRect.top + targetRect.height / 2 - tooltipH / 2;
          break;
        case 'bottom':
          left = targetRect.left + targetRect.width / 2 - TOOLTIP_W / 2;
          top = targetRect.bottom + OFFSET;
          break;
        case 'top':
          left = targetRect.left + targetRect.width / 2 - TOOLTIP_W / 2;
          top = targetRect.top - OFFSET - tooltipH;
          break;
        default:
          break;
      }

      // Clamp to viewport
      if (left + TOOLTIP_W > vw - MARGIN) left = vw - MARGIN - TOOLTIP_W;
      if (left < MARGIN) left = MARGIN;
      if (top + tooltipH > vh - MARGIN) top = vh - MARGIN - tooltipH;
      if (top < MARGIN) top = MARGIN;

      setStyle({
        position: 'fixed',
        zIndex: 2010,
        left,
        top,
        opacity: 1,
        transition: 'opacity 0.2s, left 0.2s, top 0.2s',
      });
    });
  }, [targetRect, position, stepIndex]);

  const arrowPositionClass = `tutorial-component-tooltip-arrow-${position}`;

  return (
    <div ref={tooltipRef} style={style}>
      <div className="tutorial-component-tooltip-card">
        <div className={`tutorial-component-tooltip-arrow ${arrowPositionClass}`} />

        {/* Step indicator + close */}
        <div className="tutorial-component-tooltip-header">
          <span className="tutorial-component-tooltip-step-label">
            {t.tutorial?.step || 'Step'} {stepIndex + 1} {t.tutorial?.of || 'of'} {totalSteps}
          </span>
          <button onClick={onFinish} className="tutorial-component-tooltip-close">
            <FaTimes />
          </button>
        </div>

        {/* Progress bar */}
        <div className="tutorial-component-tooltip-progress-track">
          <div
            className="tutorial-component-tooltip-progress-fill"
            style={{ width: `${((stepIndex + 1) / totalSteps) * 100}%` }}
          />
        </div>

        {/* Content */}
        <h3 className="tutorial-component-tooltip-title">{title}</h3>
        <p className="tutorial-component-tooltip-desc">{description}</p>

        {/* Navigation */}
        <div className="tutorial-component-tooltip-nav">
          <button
            onClick={onPrev}
            disabled={isFirst}
            className="tutorial-component-tooltip-btn tutorial-component-tooltip-btn-ghost"
          >
            <FaChevronLeft style={{ marginRight: 4 }} />
            {t.tutorial?.prev || 'Previous'}
          </button>
          <button
            onClick={isLast ? onFinish : onNext}
            className="tutorial-component-tooltip-btn tutorial-component-tooltip-btn-primary"
          >
            {isLast ? (t.tutorial?.finish || 'Finish') : (t.tutorial?.next || 'Next')}
            {!isLast && <FaChevronRight style={{ marginLeft: 4 }} />}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TutorialOverlay() {
  const {
    isTutorialActive,
    currentStep,
    totalSteps,
    currentStepData,
    nextStep,
    prevStep,
    finishTutorial,
  } = useTutorial();
  const t = useTranslations(translations);
  const [dotPositions, setDotPositions] = useState(new Map());
  const [activeTargetRect, setActiveTargetRect] = useState(null);

  const calculatePositions = useCallback(() => {
    const positions = new Map();

    TUTORIAL_STEPS.forEach((step) => {
      const el = document.querySelector(step.targetSelector);
      if (el) {
        const rect = el.getBoundingClientRect();
        positions.set(step.id, {
          x: rect.left + rect.width / 2,
          y: rect.top + rect.height / 2,
          visible: rect.width > 0 && rect.height > 0,
        });
      } else {
        positions.set(step.id, { x: 0, y: 0, visible: false });
      }
    });

    setDotPositions(positions);

    if (currentStepData) {
      const activeEl = document.querySelector(currentStepData.targetSelector);
      if (activeEl) {
        setActiveTargetRect(activeEl.getBoundingClientRect());
      } else {
        setActiveTargetRect(null);
      }
    }
  }, [currentStepData]);

  useEffect(() => {
    if (!isTutorialActive) return;
    // Slightly longer delay to let sidebar section expand animation complete
    const initTimer = setTimeout(calculatePositions, 350);
    window.addEventListener('resize', calculatePositions);
    window.addEventListener('scroll', calculatePositions, true);
    const interval = setInterval(calculatePositions, 500);
    return () => {
      clearTimeout(initTimer);
      window.removeEventListener('resize', calculatePositions);
      window.removeEventListener('scroll', calculatePositions, true);
      clearInterval(interval);
    };
  }, [isTutorialActive, calculatePositions]);

  if (!isTutorialActive || !currentStepData) return null;

  const stepTitle = t.tutorial?.[currentStepData.titleKey] || currentStepData.titleKey;
  const stepDesc = t.tutorial?.[currentStepData.descriptionKey] || currentStepData.descriptionKey;

  return (
    <>
      {/* Click blocker */}
      <div className="tutorial-component-click-blocker" />

      {/* Semi-transparent backdrop */}
      <div className="tutorial-component-backdrop" />

      {/* Highlight cutout for active element */}
      {activeTargetRect && (
        <div
          className="tutorial-component-highlight-cutout"
          style={{
            left: activeTargetRect.left - 4,
            top: activeTargetRect.top - 4,
            width: activeTargetRect.width + 8,
            height: activeTargetRect.height + 8,
          }}
        />
      )}

      {/* Pulsing dot for current step only */}
      {(() => {
        const pos = dotPositions.get(currentStepData.id);
        if (!pos || !pos.visible) return null;
        return (
          <TutorialDot
            key={currentStepData.id}
            x={pos.x}
            y={pos.y}
            isActive={true}
          />
        );
      })()}

      {/* Tooltip for current step */}
      <TutorialTooltip
        stepIndex={currentStep}
        totalSteps={totalSteps}
        title={stepTitle}
        description={stepDesc}
        position={currentStepData.position}
        targetRect={activeTargetRect}
        onNext={nextStep}
        onPrev={prevStep}
        onFinish={finishTutorial}
        t={t}
      />
    </>
  );
}
